"use client";

import React, { useState, useEffect } from "react";
import axios from "axios";
import { FiUploadCloud } from "react-icons/fi";
import { ToastContainer, toast } from "react-toastify";

export default function GoldDistributionListManager() {
  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  const [pdfList, setPdfList] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch PDF list
  const fetchPdfs = async () => {
    try {
      const response = await axios.get(
        `${apiUrl}/api/v1/gold-distribution/get`
      );
      setPdfList(response.data);
    } catch (error) {
      console.error("Error fetching PDFs:", error);
    }
  };

  useEffect(() => {
    fetchPdfs();
  }, []);

  // Upload / Edit PDF
  const uploadPdf = async () => {
    if (!selectedFile) {
      toast.dismiss();
      toast.error("Select a PDF first");
      return;
    }

    const formData = new FormData();
    formData.append("pdf", selectedFile);

    // If editing, include ID
    if (editItem) {
      formData.append("id", editItem.id);
    }

    try {
      setLoading(true);

      await axios.post(
        `${apiUrl}/api/v1/gold-distribution/add-edit`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      toast.dismiss();
      toast.success(
        editItem ? "PDF updated successfully!" : "PDF uploaded successfully!"
      );

      setShowUploadModal(false);
      setSelectedFile(null);
      setEditItem(null);
      fetchPdfs();
    } catch (error) {
      console.error("Upload error:", error);
      toast.dismiss();
      toast.error("Failed to upload PDF");
    } finally {
      setLoading(false);
    }
  };

  // Start editing
  const editPdf = item => {
    setEditItem(item);
    setSelectedFile(null);
    setShowUploadModal(true);
  };

  // Delete PDF
  const deletePdf = async id => {
    if (!window.confirm("Are you sure you want to delete this PDF?")) return;

    try {
      await axios.delete(`${apiUrl}/api/v1/gold-distribution/delete/${id}`);
      toast.dismiss();
      toast.success("PDF deleted successfully!");
      fetchPdfs();
    } catch (error) {
      console.error("Delete error:", error);
      toast.dismiss();
      toast.error("Failed to delete PDF");
    }
  };

  return (
    <div className="min-h-screen p-6 bg-gray-100 mt-10">
      <ToastContainer />
      <div className="flex justify-between w-full mb-6">
        <h1 className="text-xl font-bold text-start text-[#754C28]">
          Gold Distribution List – PDF Manager
        </h1>

        <button
          className="bg-blue-600 text-white px-4 py-2 rounded"
          onClick={() => {
            setEditItem(null);
            setSelectedFile(null);
            setShowUploadModal(true);
          }}
        >
          Upload PDF
        </button>
      </div>

      {/* PDF List */}
      <div className="grid grid-cols-1 gap-6">
        {pdfList.map(pdf => (
          <div
            key={pdf.id}
            className="bg-white p-4 rounded shadow flex flex-col items-start"
          >
            <p className="font-semibold mb-2 text-start truncate max-w-[280px]">
              {pdf.pdf_url}
            </p>

            <div className="flex gap-2 flex-wrap">
              <a
                href={`${pdf.pdf_url}`}
                target="_blank"
                className="bg-blue-600 text-white px-3 py-1 rounded"
              >
                Preview
              </a>

              <button
                className="bg-green-600 text-white px-3 py-1 rounded"
                onClick={() => editPdf(pdf)}
              >
                Edit
              </button>

              <button
                className="bg-red-600 text-white px-3 py-1 rounded"
                onClick={() => deletePdf(pdf.id)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Upload / Edit Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-md p-6 rounded relative">
            <button
              className="absolute top-2 right-2 bg-red-500 text-white px-3 py-1 rounded"
              onClick={() => setShowUploadModal(false)}
            >
              Close
            </button>

            <h2 className="text-xl font-semibold mb-4">
              {editItem ? "Replace PDF" : "Upload PDF"}
            </h2>

            {editItem && (
              <div className="mb-4">
                <p className="font-semibold mb-2">Previous PDF:</p>
                <iframe
                  src={`${apiUrl}/${editItem.pdf_url}`}
                  className="w-full h-[200px] border rounded"
                />
              </div>
            )}

            <div className="mb-4">
              <p className="font-semibold mb-2">
                {selectedFile
                  ? `Selected: ${selectedFile.name}`
                  : "Choose PDF:"}
              </p>

              <input
                type="file"
                accept="application/pdf"
                id="pdfInput"
                onChange={e => setSelectedFile(e.target.files[0])}
                className="hidden"
              />

              <label
                htmlFor="pdfInput"
                className="flex items-center gap-2 cursor-pointer bg-gray-200 px-4 py-2 rounded hover:bg-gray-300"
              >
                <FiUploadCloud size={24} className="text-blue-800" />
                {selectedFile ? "Change PDF" : "Upload PDF"}
              </label>
            </div>

            <button
              className="bg-blue-600 text-white px-4 py-2 rounded w-full"
              onClick={uploadPdf}
              disabled={loading}
            >
              {loading
                ? "Uploading..."
                : editItem
                ? "Replace PDF"
                : "Upload PDF"}
            </button>
          </div>
        </div>
      )}

      {/* Preview Large */}
      {previewPdf && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-3xl p-4 rounded relative">
            <button
              className="absolute top-2 right-2 bg-red-500 text-white px-3 py-1 rounded"
              onClick={() => setPreviewPdf(null)}
            >
              Close
            </button>

            <iframe src={previewPdf} className="w-full h-[500px]" />
          </div>
        </div>
      )}
    </div>
  );
}
